/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */
import React, {useRef, useEffect} from 'react';
import {Box, Typography, useMediaQuery, useTheme} from '@mui/material';
import {getRefTagAndOrigin, ImgNextGen} from './homeCategory';
import Slider from 'react-slick';
import {IconTag} from './constants';
import {handleTracking} from '@jobseeker/common-core/utils/analytics';
import {deferTask} from '@jobseeker/common-core/utils/common';
import {useIsLargeDevice} from '@jobseeker/lotus/hooks';
import usePauseAnimation from './usePauseAnimation';

const createMarkup = (title: any) => {
  return {__html: title};
};

const s3Url = 'https://staticlogo.hirist.com';

interface HomePageCompanyIconsProps {
  isTechDrive?: boolean;
  companyLogos?: string[];
}

const HomePageCompanyIcons: React.FC<HomePageCompanyIconsProps> = ({
  isTechDrive = false,
  companyLogos = [],
}) => {
  const content = (isTechDrive ? companyLogos : IconTag).map((icon, indx) => {
    if (indx % 2 === 1) return null;
    return (
      <div key={indx} className="top-companies-icon-wrapper">
        <div className="top-tech-company-icon">
          <ImgNextGen
            width={'160px'}
            style={{marginBottom: '38px'}}
            srcWebp={
              isTechDrive
                ? companyLogos[indx]
                : `${s3Url}/webp/${IconTag[indx].CompanyIconTag}.webp`
            }
            fallback={
              isTechDrive
                ? companyLogos[indx]
                : `${s3Url}/${IconTag[indx].CompanyIconTag}.png`
            }
          />
        </div>
        {indx + 1 < (isTechDrive ? companyLogos : IconTag).length && (
          <div className="top-tech-company-icon">
            <ImgNextGen
              width={'160px'}
              srcWebp={
                isTechDrive
                  ? companyLogos[indx + 1]
                  : `${s3Url}/webp/${IconTag[indx + 1].CompanyIconTag}.webp`
              }
              fallback={
                isTechDrive
                  ? companyLogos[indx + 1]
                  : `${s3Url}/${IconTag[indx + 1].CompanyIconTag}.png`
              }
            />
          </div>
        )}
      </div>
    );
  });

  const Header = isTechDrive
    ? 'Top Previous Hiring Partners'
    : "More than <b style='font-weight:800'>50k recruiters</b> from leading tech companies are hiring";

  const nextSlide = () => {
    slider.current?.slickNext();
  };

  const prevSlide = () => {
    slider.current?.slickPrev();
  };

  const theme = useTheme();
  const isLarge = useIsLargeDevice(1024);
  const isLargeScreen = useMediaQuery(theme.breakpoints.up(1096)); // 1096px and above
  const isMediumScreen = useMediaQuery(theme.breakpoints.between(867, 1095)); // Between 867px and 1095px
  const isSmallScreen = useMediaQuery(theme.breakpoints.between(696, 866)); // Between 696px and 866px
  const isExtraSmallScreen = useMediaQuery(theme.breakpoints.between(545, 695)); // Between 545px and 695px
  const isTinyScreen = useMediaQuery(theme.breakpoints.between(360, 544)); // Between 360px and 544px

  const noOfSlidesToShow = () => {
    if (isLargeScreen) return 5;
    if (isMediumScreen) return 5;
    if (isSmallScreen) return 4;
    if (isExtraSmallScreen) return 3;
    if (isTinyScreen) return 2;

    return 1;
  };
  const slider = useRef<Slider | null>();
  const sliderWrapperRef = useRef<HTMLDivElement>(null);

  // Autoplay keeps firing slide changes; pause it during taps, off-screen and
  // in hidden tabs. slickPause/slickPlay setState inside react-slick, so run
  // them after the tap's paint rather than inside the pointerdown listener.
  usePauseAnimation(sliderWrapperRef, {
    pause: () => deferTask(() => slider.current?.slickPause()),
    resume: () => slider.current?.slickPlay(),
  });

  const settings = {
    dots: false,
    speed: 500,
    slidesToShow: noOfSlidesToShow(),
    slidesToScroll: 1,
    adaptiveHeight: false,
    arrows: false,
    autoplay: true,
    infinite: true,
    appendDots: (dots: any) => (
      <div>
        <div className="dots-wraper">
          <span className="dotaround" onClick={prevSlide}></span>
          <span className="dotcenter"></span>
          <span className="dotaround" onClick={nextSlide}></span>
        </div>
      </div>
    ),
  };

  useEffect(() => {
    const {origin, refText} = getRefTagAndOrigin();

    handleTracking('jsViewTopCompaniesPanel', {
      event_category: 'jsHomePage',
      Source: `${origin}`,
      Device: sessionStorage.getItem('device') || 'desktop',
      Ref: `${refText}`,
    });
  }, []);
  return (
    <Box
      className="top-company-icons-main-wrapper"
      sx={{
        padding: {xs: '0 20px', sm: '0 41px', md: '0 68px'},
        marginBottom: {xs: 0, sm: '60px'},
        '@media(max-width: 1023px)': {
          width: '100%',
        },
      }}
    >
      <Box
        className="top-companies-heading-txt"
        sx={{
          textAlign: 'center',
          mb: isTechDrive ? 2.5 : 2,
          fontSize: {xs: '24px', sm: '32px'},
          fontFamily: 'Nunito Sans',
          fontWeight: 'bold',
          color: '#000',
        }}
      >
        <Typography
          component={'h2'}
          sx={{
            textAlign: 'center',
            mb: isTechDrive ? 1 : 2,
            fontSize: isTechDrive
              ? isLarge
                ? '32px'
                : '24px'
              : {md: '32px', xxs: '32px'},
            fontFamily: 'Nunito Sans',
            fontWeight: 'bold',
            color: '#000',
            lineHeight: isTechDrive ? 1.2 : {xxs: 1.2, md: 1.5},
          }}
          dangerouslySetInnerHTML={createMarkup(Header)}
        />
      </Box>
      <Box ref={sliderWrapperRef} sx={{ml: 2, mr: {xl: -7, lg: -3}}}>
        <Slider ref={c => (slider.current = c)} {...settings}>
          {content}
        </Slider>
      </Box>
    </Box>
  );
};

export default React.memo(HomePageCompanyIcons);
